import { useState } from 'react'
import {
  Headphones,
  Loader2,
  Mic,
  RefreshCw,
  Volume2,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { getDevice } from '@/lib/twilioDeviceSingleton'
import { useAudioDevices } from '@/hooks/useAudioDevices'

interface Props {
  collapsed: boolean
  darkMode: boolean
  /** Petit bouton icône pour la barre d'outils du bas. */
  compact?: boolean
}

export function AudioDevicePopover({ collapsed, darkMode, compact = false }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const device = getDevice()
  const {
    microphones,
    speakers,
    currentMicrophone,
    currentSpeaker,
    changeMicrophone,
    changeSpeaker,
    refreshDevices,
    requestPermission,
    hasPermission,
    isRefreshing,
  } = useAudioDevices(device)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          compact
            ? 'flex items-center justify-center h-7 w-8 rounded-md transition-colors'
            : 'w-full flex items-center py-2 rounded-xl text-sm transition-colors',
          !compact && (collapsed ? 'justify-center px-2' : 'gap-3 px-4'),
          darkMode
            ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
          open && (darkMode ? 'bg-slate-800 text-white' : 'bg-slate-100'),
        )}
        title={t('sidebar.audioDevice', 'Périphériques audio')}
      >
        <Headphones className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        {!collapsed && !compact && <span>{t('sidebar.audioDevice', 'Périphériques audio')}</span>}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              'absolute z-50 left-full ml-2 bottom-0 w-72 rounded-xl shadow-2xl border p-3',
              darkMode
                ? 'bg-slate-900 border-slate-700 text-slate-200'
                : 'bg-white border-slate-200 text-slate-900',
            )}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">
                {t('audio.title', 'Périphériques audio')}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => void refreshDevices()}
                  disabled={isRefreshing}
                  className={cn(
                    'p-1 rounded transition-colors',
                    darkMode
                      ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                  )}
                  title={t('audio.refresh', 'Rafraîchir')}
                >
                  {isRefreshing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'p-1 rounded transition-colors',
                    darkMode
                      ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                  )}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {!hasPermission && (
              <button
                type="button"
                onClick={() => void requestPermission()}
                className="w-full mb-3 px-3 py-2 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                {t('audio.grantPermission', 'Autoriser le microphone')}
              </button>
            )}

            <div className="space-y-3">
              {/* Microphone */}
              <div>
                <div
                  className={cn(
                    'flex items-center gap-1.5 mb-1.5 text-[10px] uppercase font-semibold tracking-wider',
                    darkMode ? 'text-slate-500' : 'text-slate-400',
                  )}
                >
                  <Mic className="h-3 w-3" />
                  {t('audio.microphone', 'Microphone')}
                </div>
                {microphones.length === 0 ? (
                  <p
                    className={cn(
                      'text-xs italic',
                      darkMode ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    {t('audio.noMic', 'Aucun microphone détecté')}
                  </p>
                ) : (
                  <div className="space-y-0.5">
                    {microphones.map((mic) => (
                      <DeviceRadio
                        key={mic.deviceId}
                        label={mic.label || `Microphone ${mic.deviceId.slice(0, 6)}`}
                        selected={currentMicrophone === mic.deviceId}
                        onClick={() => void changeMicrophone(mic.deviceId)}
                        darkMode={darkMode}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Speaker */}
              <div>
                <div
                  className={cn(
                    'flex items-center gap-1.5 mb-1.5 text-[10px] uppercase font-semibold tracking-wider',
                    darkMode ? 'text-slate-500' : 'text-slate-400',
                  )}
                >
                  <Volume2 className="h-3 w-3" />
                  {t('audio.speaker', 'Haut-parleur')}
                </div>
                {speakers.length === 0 ? (
                  <p
                    className={cn(
                      'text-xs italic',
                      darkMode ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    {t('audio.noSpeaker', 'Aucun haut-parleur détecté')}
                  </p>
                ) : (
                  <div className="space-y-0.5">
                    {speakers.map((spk) => (
                      <DeviceRadio
                        key={spk.deviceId}
                        label={spk.label || `Haut-parleur ${spk.deviceId.slice(0, 6)}`}
                        selected={currentSpeaker === spk.deviceId}
                        onClick={() => void changeSpeaker(spk.deviceId)}
                        darkMode={darkMode}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

interface DeviceRadioProps {
  label: string
  selected: boolean
  onClick: () => void
  darkMode: boolean
}

function DeviceRadio({ label, selected, onClick, darkMode }: DeviceRadioProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors',
        selected
          ? darkMode
            ? 'bg-indigo-500/20 text-indigo-200'
            : 'bg-indigo-50 text-indigo-700'
          : darkMode
            ? 'text-slate-300 hover:bg-slate-800'
            : 'text-slate-700 hover:bg-slate-100',
      )}
    >
      <span
        className={cn(
          'w-3 h-3 rounded-full border-2 flex-shrink-0',
          selected
            ? 'bg-indigo-600 border-indigo-600'
            : darkMode
              ? 'border-slate-600'
              : 'border-slate-300',
        )}
      />
      <span className="truncate">{label}</span>
    </button>
  )
}
